import React, { useEffect, useState } from "react";
import QRCode from "qrcode.react";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

import { Dialog, DialogContent, Paper, Typography, IconButton, Box, makeStyles } from "@material-ui/core";
import { Add, Remove, Replay } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    header: {
        textAlign: 'center',
        marginBottom: theme.spacing(1),
        "& h6": {
            fontWeight: 600,
            color: "#1a202c",
            letterSpacing: "0.3px",
            margin: 0,
            fontSize: "1.3rem",
        },
        "& p": {
            color: "#718096",
            fontSize: "0.85rem",
            margin: theme.spacing(0.5, 0, 0, 0),
            fontWeight: 500,
        }
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5),
        background: '#f7fafc',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
    },
    iconButton: {
        color: theme.palette.primary.main,
        transition: 'all 0.3s ease',
        '&:hover': {
            background: theme.palette.primary.main,
            color: 'white',
            transform: 'scale(1.15)',
        },
        '&:disabled': {
            color: '#cbd5e0',
            opacity: 0.5,
        },
    },
    resetButton: {
        fontWeight: 700,
        fontSize: "1.1rem",
        color: theme.palette.primary.main,
        transition: 'all 0.3s ease',
        marginLeft: theme.spacing(1),
        '&:hover': {
            background: theme.palette.primary.main,
            color: 'white',
            transform: 'rotate(-360deg) scale(1.15)',
        },
    },
    sizeDisplay: {
        fontWeight: 700,
        color: theme.palette.primary.main,
        fontSize: "1.1rem",
        minWidth: '50px',
        textAlign: 'center',
    },
    qrcodeWrapper: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        background: '#f7fafc',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
    },
}));

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
    const classes = useStyles();
    const [qrCode, setQrCode] = useState("");
    const [size, setSize] = useState(500);
    const MIN_SIZE = 250;
    const MAX_SIZE = 800;

    useEffect(() => {
        const fetchSession = async () => {
            if (!whatsAppId) return;

            try {
                const { data } = await api.get(`/whatsapp/${whatsAppId}`);
                setQrCode(data.qrcode);
            } catch (err) {
                toastError(err);
            }
        };
        fetchSession();
    }, [whatsAppId]);

    useEffect(() => {
        if (!whatsAppId) return;
        const socket = openSocket();

        socket.on("whatsappSession", data => {
            if (data.action === "update" && data.session.id === whatsAppId) {
                setQrCode(data.session.qrcode);
            }

            if (data.action === "update" && data.session.qrcode === "") {
                onClose();
            }
        });

        return () => {
            socket.disconnect();
        };
    }, [whatsAppId, onClose]);

    const handleIncreaseSize = () => {
        setSize(prevSize => Math.min(prevSize + 50, MAX_SIZE));
    };

    const handleDecreaseSize = () => {
        setSize(prevSize => Math.max(prevSize - 50, MIN_SIZE));
    };

    const handleResetSize = () => {
        setSize(500);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" scroll="paper">
            <DialogContent style={{ backgroundColor: "white" }}>
                <Paper style={{ backgroundColor: "white" }} elevation={0}>
                    <Box className={classes.container}>
                        <Box className={classes.header}>
                            <Typography variant="h6">
                                {i18n.t("qrCode.message")}
                            </Typography>
                            <Typography>
                                Escaneie o código QR com seu WhatsApp
                            </Typography>
                        </Box>

                        <Box className={classes.controls}>
                            <IconButton
                                onClick={handleDecreaseSize}
                                disabled={size <= MIN_SIZE}
                                size="small"
                                className={classes.iconButton}
                                title="Diminuir tamanho"
                            >
                                <Remove />
                            </IconButton>

                            <Typography className={classes.sizeDisplay}>
                                {size}
                            </Typography>

                            <IconButton
                                onClick={handleIncreaseSize}
                                disabled={size >= MAX_SIZE}
                                size="small"
                                className={classes.iconButton}
                                title="Aumentar tamanho"
                            >
                                <Add />
                            </IconButton>

                            <IconButton
                                onClick={handleResetSize}
                                size="small"
                                className={classes.resetButton}
                                title="Resetar tamanho"
                            >
                                <Replay />
                            </IconButton>
                        </Box>

                        <Box className={classes.qrcodeWrapper}>
                            {qrCode ? (
                                <QRCode value={qrCode} size={size} />
                            ) : (
                                <span>Aguardando QR Code...</span>
                            )}
                        </Box>
                    </Box>
                </Paper>
            </DialogContent>
        </Dialog>
    );
};

export default React.memo(QrcodeModal);
